import { useEffect, useMemo, useState } from "react";
import {
  noBarData,
  noData,
  validLineData,
  horizontalBarChartMargins,
  totalActivity,
  chartColorScale,
  dailyActivity,
  createDateRange,
  chartLegendLabels,
  completes,
  starts,
  isUserDeleted,
  formatUserName,
  canUseChildAccount,
  type PerformanceWithAccountFilter,
} from "../../Common/utils/performanceUtils";
import { ChartWrapper, getDateFormatByCount, HorizontalBarChart, LineChart } from "components/charts";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";

import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import dateTimeUtils from "utils/dateTimeUtils";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { useQuery } from "@tanstack/react-query";
import { getEngagementQuery, getLineChartQuery, getPeopleDetailsQuery } from "./Queries/queries";

import styles from "./embeddedVideoPerformanceBody.module.scss";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { type ColumnOption } from "interfaces/columnOptions";
import { type EmbeddedVideoPeopleDetails } from "../types/models";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { Icon } from "semantic-ui-react";
import { VideoPerformancePeopleStatusToCell } from "features/Library/Videos/Performance/constants";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { getEmbeddedVideoPeopleDetailsExport } from "./actions/embeddedVideoActions";
import { bindActionCreators } from "redux";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";

export type Props = PropsFromRedux & {
  filter: PerformanceWithAccountFilter;
  embeddedVideoId: number;
  embeddedVideoTitle: string;
  includeAccounts?: boolean;
  flowId?: number;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
};

const getColumnOptions = (
  includeAccounts?: boolean,
  childAccountId?: number,
  hasCreateAccountsAddOn?: boolean,
): ColumnOption<EmbeddedVideoPeopleDetails>[] => {
  return [
    {
      name: "Name",
      width: 3,
      isSortable: false,
      render(entity) {
        const formattedNameDisplay = formatUserName(entity.UserFirstName, entity.UserLastName);
        const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
        const shouldShowLink =
          !isUserDeleted(entity.UserFirstName, entity.UserLastName) && (!includeAccounts || childAccountEnabled);

        const userName = (
          <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
        );
        return (
          <div>
            {shouldShowLink ? (
              <MoboLink
                className={styles.linkButton}
                target={childAccountEnabled ? "_blank" : "_self"}
                accountId={childAccountEnabled ? childAccountId : undefined}
                to={`/${RouteNames.peopleUsers}/${entity.UserId}`}
              >
                {userName}
              </MoboLink>
            ) : (
              userName
            )}
          </div>
        );
      },
    },
    {
      isSortable: false,
      name: "Email",
      render(entity) {
        return (
          <Tooltip
            target={<TextTruncate className={styles.wordBreak}>{entity.UserEmail}</TextTruncate>}
            content={entity.UserEmail}
            showAlways
          />
        );
      },
      width: 3,
    },
    {
      isSortable: false,
      name: `Total ${starts}`,
      render(entity) {
        return <div>{entity.VideoStarts}</div>;
      },
      width: 3,
    },
    {
      isSortable: false,
      name: `Total ${completes}`,
      render(entity) {
        return <div>{entity.VideoCompletions}</div>;
      },
      width: 3,
    },
    {
      isSortable: false,
      name: "Date Watched",
      render(entity) {
        return <div>{entity.DateWatched ? dateTimeUtils.formatDate(entity.DateWatched) : "-"}</div>;
      },
      width: 3,
    },
    {
      isSortable: false,
      name: "Status",
      render(entity) {
        return (
          <div className={styles.status}>
            <div>
              <Icon
                name={VideoPerformancePeopleStatusToCell[entity.Status].icon}
                className={VideoPerformancePeopleStatusToCell[entity.Status].color}
              />
              &nbsp;
              {entity.Status}
            </div>
            <div>&nbsp;</div>
          </div>
        );
      },
      width: 3,
    },
  ];
};

export const EmbeddedVideoPerformanceBody: React.FC<Props> = ({
  flowId,
  filter,
  embeddedVideoId,
  setHasExportData,
  includeAccounts,
  embeddedVideoTitle,
  setExport,
  getEmbeddedVideoPeopleDetailsExport,
  setIsReportEnabled,
  resetIsReportEnabled,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  const [chartState, setChartState] = useState(totalActivity);
  const reportEnabled = !!useFeatureFlag(FeatureFlags.UserPerformance);
  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);
  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryKey: ["userCollaborationLine", { ...filter, flowId }, embeddedVideoId, chartPeriod],
    queryFn: getLineChartQuery,
    enabled: reportEnabled && chartPeriod !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });

  const engagementQuery = useQuery({
    queryKey: ["embeddedVideoEngagement", { ...filter, flowId }, embeddedVideoId],
    queryFn: getEngagementQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  const peopleDetailsQuery = useQuery({
    queryKey: ["embeddedVideoPeopleDetails", { ...filter, flowId }, embeddedVideoId],
    queryFn: getPeopleDetailsQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  useEffect(() => {
    setExport({
      method: getEmbeddedVideoPeopleDetailsExport,
      args: [embeddedVideoId, { ...filter, flowId }, embeddedVideoTitle],
      isExportEnabled: true,
    });
  }, [filter, getEmbeddedVideoPeopleDetailsExport, setExport, embeddedVideoId, embeddedVideoTitle, flowId]);

  const columnOptions = getColumnOptions(includeAccounts, childAccountId, hasCreateAccountsAddOn);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: peopleDetailsQuery.isSuccess && !!peopleDetailsQuery.data.length,
      tooltipContent: "No data available for export. This embedded video contains no data.",
    });
  }, [setHasExportData, peopleDetailsQuery.isSuccess, peopleDetailsQuery.data]);

  const lineData = useMemo(() => {
    if (chartState === dailyActivity) {
      return {
        xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
        yData: [lineChartQuery.data?.StartedCount, lineChartQuery.data?.CompletedCount],
      };
    }
    return {
      xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
      yData: [lineChartQuery.data?.CumulativeStartedCount, lineChartQuery.data?.CumulativeCompletedCount],
    };
  }, [chartState, lineChartQuery.data]);

  return (
    <div className={styles.root}>
      <div className={styles.activity} ref={measureRef}>
        <ChartWrapper
          titles={["Total Activity", "Daily Activity"]}
          legendLabels={chartLegendLabels}
          showLegend
          onChange={setChartState}
          colorRange={chartColorScale}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([
              lineChartQuery.data.CumulativeCompletedCount,
              lineChartQuery.data.CumulativeStartedCount,
            ]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xFormatterFunc={getDateFormatByCount(lineChartQuery.data.Date.length)}
                colorRange={chartColorScale}
                {...lineData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.engagementQuery}>
        <ChartWrapper titles={["Engagement"]} legendLabels={chartLegendLabels} showLegend colorRange={chartColorScale}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && !noBarData(...engagementQuery.data.usageBarData.map((d) => d.value)) ? (
              <HorizontalBarChart
                {...sharedAccountReportingHorizontalBarProps}
                margins={{ ...horizontalBarChartMargins }}
                data={engagementQuery.data.usageBarData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cards} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <RequestStatusRenderer state={peopleDetailsQuery.status}>
          {peopleDetailsQuery.isSuccess && (
            <GenericPerformanceList
              title="People"
              rows={peopleDetailsQuery.data}
              columns={columnOptions}
              filter={filter}
            />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  setExport: bindAction(setExportAction, dispatch),
  getEmbeddedVideoPeopleDetailsExport: bindActionCreators(getEmbeddedVideoPeopleDetailsExport, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EmbeddedVideoPerformanceBody);
export default ConnectedComponent;
